/**
 * The IP counts the header's Locations menu prints, on the server and again in
 * the browser.
 *
 * The header is drawn on every marketing page and belongs to no route, so no
 * loader carries them. The server entry reads them before it renders a
 * marketing page (`src/server.ts`, from the countries
 * `locationCollection.ts` holds) and answers with them while that page renders
 * (`src/routing/requestLocationCounts.ts`); the header then writes the ones it
 * prints into the page, and the browser reads them back from there before it
 * hydrates, as it learns the preview flag (`__root.tsx`). This module stays
 * free of the server's side so that code the browser gets may import it.
 */
declare global {
  interface Window {
    __LOCATION_COUNTS__?: Record<string, string>;
  }
}

/** The counts by country code; undefined where none were read. */
type Counts = Readonly<Record<string, string>> | undefined;

let readOnServer: () => Counts = () => undefined;

/** Installed by the server-only side, which holds the request scope. */
export function answerLocationCounts(reader: () => Counts): void {
  readOnServer = reader;
}

/** The counts this page was rendered with. */
export function locationCounts(): Counts {
  return typeof window === "undefined"
    ? readOnServer()
    : window.__LOCATION_COUNTS__;
}

/**
 * The line that hands the counts to the browser: a script setting them before
 * the page hydrates. Escaped for a script element, so a count can never close
 * it.
 */
export function locationCountsScript(counts: Readonly<Record<string, string>>) {
  const json = JSON.stringify(counts)
    .replace(/</g, "\\u003c")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
  return `window.__LOCATION_COUNTS__=${json}`;
}
